import { Loader } from '@10xLabs/react-web/components/Loader'
import { useMutation } from '@apollo/client'
import {
  DndContext,
  useSensor,
  TouchSensor,
  useSensors,
  MouseSensor,
  DragEndEvent,
  closestCenter,
  DragOverlay,
  DragStartEvent,
  DragOverEvent
} from '@dnd-kit/core'
import { useMap } from '@vis.gl/react-google-maps'
import { createContext, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Label } from 'components/v2/Label'
import { Row } from 'components/v2/Row'
import {
  ChangeRoutingProjectPlanOptimizationRouteStopsPlanOptimizationInput,
  RoutingProjectDow,
  RoutingProjectPlanStatus
} from 'gql/generated/graphql'
import { DatasetLocationsList } from 'features/RoutingProjects/components/RoutingProjectSidebar/DatasetLocationsList'
import { RoutingProjectSidebarActions } from 'features/RoutingProjects/components/RoutingProjectSidebar/Actions'
import { SelectRoutingProjecPlan } from 'features/RoutingProjects/components/SelectRoutingProjectPlan'
import {
  IDraggedLocation,
  ILocationRemove,
  IRoutingProjectDataset,
  IRoutingProjectDatasetLocation,
  IRoutingProjectModel,
  IRoutingProjectPlan,
  IRoutingProjectPlanModel,
  IRoutingProjectPlanOptimization,
  IRoutingProjectPlanOptimizationRoute
} from 'features/RoutingProjects/types'
import EmptyState from 'components/v2/EmptyState'
import { Button } from 'components/v2/Button'
import { isEmpty, isNull, isNumber, uniq } from 'lodash'
import { OptimizedRoutesList } from 'features/RoutingProjects/components/RoutingProjectSidebar/OptimizedRoutesList'
import { OrphansLocationsCard } from 'features/RoutingProjects/components/RoutingProjectSidebar/OrphansLocationsCard'
import { OrphansLocationsList } from 'features/RoutingProjects/components/RoutingProjectSidebar/OrphansLocationsList'
import { LocationItem } from 'features/RoutingProjects/components/LocationItem'
import {
  getOptimizationByDow,
  moveLocationInsidePlanOptimizationBetweenContainers,
  moveLocationInsidePlanOptimizationInsideSameContainer,
  updateRoutesPolyline
} from 'features/RoutingProjects/models'

import { CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_STOPS } from 'features/RoutingProjects/mutations'

import {
  adjustMapBoundsByRoutes,
  getAvailableDowsByRoutesAndLocations,
  getOptimizationsInputByOptimization
} from 'features/RoutingProjects/utils'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'
import { SelectDow } from 'features/RoutingProjects/components/RoutingProjectSidebar/SelectDow'
import { WarningNotification } from 'components/WarningNotification'
import { LocationDragEndModal } from 'features/RoutingProjects/components/LocationDragEndModal'
import {
  getUpdatedSkippedLocations,
  updateRoutesByTouchedRoutes
} from 'features/RoutingProjects/components/RoutingProjectSidebar/utils'
import { LocationRemoveModal } from 'features/RoutingProjects/components/LocationRemoveModal'
import { PlanActions } from 'features/RoutingProjects/components/PlanActions'
import { ExportingPlanStatus, PlanExportedStatus } from 'features/RoutingProjects/components/ExportPlanStatus'
import { SidebarOverlay } from 'features/RoutingProjects/components/RoutingProjectSidebar/SidebarOverlay'
import { RouteItem } from 'features/RoutingProjects/components/RoutingProjectSidebar/OptimizedRoutesList/RouteItem'

const SidebarWrapper = styled.div`
  position: relative;
`

const SidebarContainer = styled.div<{ isExpanded: boolean }>`
  padding: 16px;
  height: ${({ isExpanded }): string => (isExpanded ? 'calc(100% - 32px);' : 'min-content')};
  display: grid;
  grid-template-rows: min-content ${({ isExpanded }): string => (isExpanded ? '1fr' : '')};
  gap: 20px;
  position: ${({ isExpanded }): string => (isExpanded ? 'relative' : 'absolute')};
  z-index: ${({ isExpanded }): string => (isExpanded ? '0' : '1')};
  background-color: #fff;
  top: ${({ isExpanded }): string => (isExpanded ? '0' : '24px')};
  left: ${({ isExpanded }): string => (isExpanded ? '0' : '13px')};
  border-radius: 16px;
  min-width: 360px;
`

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const SidebarContent = styled.div`
  height: 0;
  min-height: 100%;
  overflow: auto;
  margin: 0 -10px;
  padding: 0 10px;
`

const PlanContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`

const SelectRoutingProjecPlanContainer = styled.div`
  flex: 1;
`

interface ISidebarContextValue {
  overlayDisplayedRouteId: string | null
  handleShowOrphansLocationsList: () => void
  handleShowRouteInOverlay: (routeId: string) => void
  handleCloseOverlay: () => void
}

const SidebarContext = createContext<ISidebarContextValue | undefined>(undefined)

export const useSidebarContext = (): ISidebarContextValue => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebarContext must be used within a SidebarContextProvider')
  }
  return context
}

interface IRoutingProjectSidebarProps {
  routingProjectModel: IRoutingProjectModel
  planModel: IRoutingProjectPlanModel
  currentOptimization: IRoutingProjectPlanOptimization | null
  planLoading: boolean
  refetchPlan: () => void
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
  selectedPlan: IRoutingProjectPlan | null
  setSelectedPlan: (plan: IRoutingProjectPlan | null) => void
  setPlanModel: (planModel: IRoutingProjectPlanModel) => void
}

export const RoutingProjectSidebar = ({
  routingProjectModel,
  planModel,
  currentOptimization,
  planLoading,
  refetchPlan,
  isExpanded,
  setIsExpanded,
  selectedPlan,
  setSelectedPlan,
  setPlanModel
}: IRoutingProjectSidebarProps): ReactElement => {
  const map = useMap()
  const {
    saveUpdatedRoutes,
    selectedDow,
    setSelectedDow,
    isStartingPlanExport,
    hasStartedPlanExport,
    isExportingPlan,
    setHasStartedPlanExport
  } = usePlanContext()
  const sensors = useSensors(useSensor(TouchSensor), useSensor(MouseSensor))
  const [planStatus, setPlanStatus] = useState<RoutingProjectPlanStatus>(RoutingProjectPlanStatus.Simulation)
  const [isShowingOrphansLocationsList, setIsShowingOrphansLocationsList] = useState(false)
  const [overlayDisplayedRouteId, setOverlayDisplayedRouteId] = useState<string | null>(null)
  const [draggedLocation, setDraggedLocation] = useState<IDraggedLocation | null>(null)
  const [planModelCopy, setPlanModelCopy] = useState<IRoutingProjectPlanModel>({ ...planModel })
  const [isOpenDragEndModal, setIsOpenDragEndModal] = useState(false)
  const [availableDowsToApplyChange, setAvailableDowsToApplyChange] = useState<RoutingProjectDow[]>([])
  const [touchedRoutesOnDragAndDrop, setTouchedRoutesOnDragAndDrop] = useState<IRoutingProjectPlanOptimizationRoute[]>(
    []
  )
  const [locationIdToRemove, setLocationIdToRemove] = useState<string | null>(null)
  const [updatedLocation, setUpdatedLocation] = useState<IRoutingProjectDatasetLocation | null>(null)
  useEffect(() => {
    setPlanModelCopy({ ...planModel })
  }, [planModel])

  const [changeRoutingProjectPlanRouteStops] = useMutation(CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_STOPS, {
    onError() {
      // Do nothing
    }
  })

  const currentOptimizationCopy = useMemo(() => {
    if (selectedDow) {
      return getOptimizationByDow(planModelCopy.optimizations, selectedDow)
    }
    return null
  }, [planModelCopy, selectedDow])

  const datasetIsUploading = !planLoading && planModel.id && isEmpty(planModel.dataset?.locations)
  const datasetReady = !planLoading && planModel.id && !isEmpty(planModel.dataset?.locations)
  const canSelectOrphans =
    !isEmpty(currentOptimizationCopy?.routes) && !isEmpty(currentOptimizationCopy?.skippedLocations) && !planLoading

  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event
    if (isEmpty(draggedLocation)) {
      setDraggedLocation({
        sortableItemId: active.id.toString(),
        location: active.data.current?.location as IRoutingProjectDatasetLocation,
        locationIndex: active.data.current?.locationIndex as number,
        routeIndex: active.data.current?.routeIndex as number | null
      })
    }
  }

  const handleDragOver = (event: DragOverEvent): void => {
    const { active, over } = event

    const draggedLocationId = active.data.current?.locationId
    const overLocationId = over?.data.current?.locationId
    const draggedLocationRouteIndex = active.data.current?.routeIndex
    const overRouteIndex = over?.data.current?.routeIndex
    const overRoute = currentOptimizationCopy?.routes[overRouteIndex as number]

    if (!over || overRoute?.isLocked || draggedLocationRouteIndex === overRouteIndex) {
      return
    }

    const newPlan = moveLocationInsidePlanOptimizationBetweenContainers({
      plan: planModelCopy,
      optimizationId: currentOptimizationCopy?.id as string,
      locationId: draggedLocationId,
      previousRouteIndex: draggedLocationRouteIndex,
      droppedLocationId: overLocationId,
      droppedRouteIndex: overRouteIndex
    })
    setPlanModelCopy({
      ...planModelCopy,
      ...newPlan
    })
  }

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    let newPlanModel = {
      ...planModelCopy
    }
    const draggedLocationEntity = draggedLocation?.location as IRoutingProjectDatasetLocation
    const { active, over } = event
    const draggedLocationRouteIndex = active.data.current?.routeIndex
    const overLocationRouteIndex = over?.data.current?.routeIndex

    const moveBetweenContainers = draggedLocation?.routeIndex !== over?.data.current?.routeIndex
    const skippedContainerMove = !isNumber(draggedLocation?.routeIndex) && !isNumber(over?.data.current?.routeIndex)
    const locationHasNewPosition = draggedLocation?.locationIndex !== over?.data.current?.locationIndex
    const locationHasMoved = moveBetweenContainers || (!skippedContainerMove && locationHasNewPosition)
    const overRoute = currentOptimizationCopy?.routes[overLocationRouteIndex as number]
    if (!locationHasMoved || overRoute?.isLocked) {
      setDraggedLocation(null)
      return
    }

    setUpdatedLocation(draggedLocationEntity)
    setDraggedLocation(null)
    if (active.id !== over?.id && overLocationRouteIndex === draggedLocationRouteIndex) {
      const newPlan = moveLocationInsidePlanOptimizationInsideSameContainer(
        planModelCopy,
        currentOptimizationCopy?.id as string,
        active.data.current?.locationId,
        over?.data.current?.locationId,
        draggedLocationRouteIndex
      )
      newPlanModel = {
        ...planModelCopy,
        ...newPlan
      }
      setPlanModelCopy({ ...newPlanModel })
    }
    const updatedRoutesIndex = uniq(
      [draggedLocation?.routeIndex, overLocationRouteIndex].filter((index) => !isNull(index))
    )
    const updatedRoutes = currentOptimization?.routes.filter((_, index) => updatedRoutesIndex.includes(index)) || []
    const updatedPlanModel = await updateRoutesPolyline({
      planModel: newPlanModel,
      optimizationId: currentOptimization?.id as string,
      updatedRoutesIndex
    })
    setPlanModelCopy({ ...updatedPlanModel })

    let availableDows = getAvailableDowsByRoutesAndLocations({
      routes: updatedRoutes,
      optimizations: planModel.optimizations,
      locations: [draggedLocationEntity],
      shouldFilterLockedRoutes: true
    })
    availableDows = availableDows.filter((dow) => dow !== selectedDow)
    setAvailableDowsToApplyChange(availableDows)
    if (!isEmpty(availableDows)) {
      setIsOpenDragEndModal(true)
      setTouchedRoutesOnDragAndDrop(updatedRoutes)
    } else {
      setPlanModel({ ...updatedPlanModel })
    }

    if (map && !isEmpty(updatedRoutesIndex)) {
      setTimeout(() => {
        adjustMapBoundsByRoutes({
          map,
          routes: updatedRoutes
        })
      }, 1)
    }
    saveUpdatedRoutes({
      planModel: updatedPlanModel,
      optimizationId: currentOptimization?.id as string,
      updatedRoutesIndex
    })
  }

  const handleApplyChangeOnDows = async (dows: RoutingProjectDow[]): Promise<void> => {
    let newPlanModel = { ...planModelCopy }
    let updatedRoutesIndex: number[] = []
    let updateRoutesIndexByOptimization: Record<string, number[]> = {}
    const selectedOptimization = planModelCopy.optimizations.find((opt) => opt.dow === selectedDow)

    for (const dow of dows) {
      updatedRoutesIndex = []
      newPlanModel = {
        ...newPlanModel,
        optimizations: newPlanModel.optimizations.map((optimization) => {
          if (optimization.dow === dow) {
            const newSkippedLocations = getUpdatedSkippedLocations(optimization, updatedLocation, selectedOptimization)
            const { updatedRoutes, updatedRoutesIndex: newUpdatedRoutesIndex } = updateRoutesByTouchedRoutes(
              optimization.routes,
              touchedRoutesOnDragAndDrop,
              selectedOptimization
            )
            updatedRoutesIndex = [...newUpdatedRoutesIndex]

            return {
              ...optimization,
              skippedLocations: newSkippedLocations,
              routes: updatedRoutes
            }
          }
          return optimization
        })
      }
      const updatedOptimization = getOptimizationByDow(newPlanModel.optimizations, dow)

      const planWithNewRoutesPolyline = await updateRoutesPolyline({
        planModel: newPlanModel,
        optimizationId: updatedOptimization?.id as string,
        updatedRoutesIndex
      })
      updateRoutesIndexByOptimization = {
        ...updateRoutesIndexByOptimization,
        [updatedOptimization?.id as string]: updatedRoutesIndex
      }
      newPlanModel = { ...planWithNewRoutesPolyline }
    }
    setPlanModel({ ...newPlanModel })
    setPlanModelCopy({ ...newPlanModel })

    const optimizationsInput: ChangeRoutingProjectPlanOptimizationRouteStopsPlanOptimizationInput[] = []
    for (const dow of dows) {
      const optimization = newPlanModel.optimizations.find((opt) => opt.dow === dow)
      if (optimization) {
        optimizationsInput.push(
          ...getOptimizationsInputByOptimization({
            optimization,
            updatedRoutesIndex: updateRoutesIndexByOptimization[optimization.id] || []
          })
        )
      }
    }
    await changeRoutingProjectPlanRouteStops({
      variables: {
        input: {
          id: routingProjectModel.id,
          plan: {
            id: planModel.id,
            optimizations: optimizationsInput
          }
        }
      }
    })
  }

  const handleDragEndModalSuccess = (): void => {
    setIsOpenDragEndModal(false)
  }

  const handleDragEndModalClose = (): void => {
    setPlanModel({ ...planModelCopy })
    setIsOpenDragEndModal(false)
  }

  const handleLocationRemove = async ({ locationId }: ILocationRemove): Promise<void> => {
    setLocationIdToRemove(locationId)
  }

  const handleShowOrphansLocationsList = (): void => {
    setIsShowingOrphansLocationsList(true)
    setOverlayDisplayedRouteId(null) // Close route overlay if open
  }

  const handleShowRouteInOverlay = (routeId: string): void => {
    setOverlayDisplayedRouteId(routeId)
    setIsShowingOrphansLocationsList(false) // Close orphans overlay if open
  }

  const handleCloseOverlay = (): void => {
    setIsShowingOrphansLocationsList(false)
    setOverlayDisplayedRouteId(null)
  }

  const handleToggleOrphansLocationsList = (): void => {
    if (isShowingOrphansLocationsList) {
      handleCloseOverlay()
    } else {
      handleShowOrphansLocationsList()
    }
  }
  const contextValue: ISidebarContextValue = {
    overlayDisplayedRouteId,
    handleShowOrphansLocationsList,
    handleShowRouteInOverlay,
    handleCloseOverlay
  }

  const noRoutesCreated = isEmpty(currentOptimization?.routes) && !isEmpty(currentOptimization?.skippedLocations)
  const canSelectDow = selectedDow && planModel.dataset?.hasDow
  const overlayDisplayedRoute = currentOptimizationCopy?.routes.find(
    (route) => route.id === overlayDisplayedRouteId
  ) as IRoutingProjectPlanOptimizationRoute

  return (
    <SidebarContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SidebarWrapper>
          <SidebarContainer isExpanded={isExpanded}>
            <SidebarHeader>
              <Heading.H3>
                <FormattedMessage id="routingProject.plans.title" defaultMessage="Plans" />
              </Heading.H3>
              <RoutingProjectSidebarActions
                isSidebarExpanded={isExpanded}
                setIsSidebarExpanded={setIsExpanded}
                routingProjectModel={routingProjectModel}
                setSelectedPlan={setSelectedPlan}
                setPlanStatus={setPlanStatus}
              />
            </SidebarHeader>
            {isExpanded && (
              <SidebarContent>
                <Row>
                  <Column>
                    <Label>
                      <FormattedMessage id="routingProject.selectPlan.title" defaultMessage="Plan" />
                    </Label>
                    <PlanContainer>
                      <SelectRoutingProjecPlanContainer>
                        <SelectRoutingProjecPlan
                          routingProjectModel={routingProjectModel}
                          selectedPlan={selectedPlan}
                          setSelectedPlan={(plan): void => {
                            setSelectedPlan(plan)
                            setHasStartedPlanExport(false)
                          }}
                          planStatus={planStatus}
                          setPlanStatus={setPlanStatus}
                        />
                      </SelectRoutingProjecPlanContainer>
                      <PlanActions planModel={planModel} routingProjectModel={routingProjectModel} />
                    </PlanContainer>
                  </Column>
                </Row>
                {(hasStartedPlanExport || isStartingPlanExport) && (
                  <Row>
                    <Column>
                      <EmptyState.Container noMargin>
                        {isStartingPlanExport || isExportingPlan ? (
                          <ExportingPlanStatus
                            planId={planModel?.id}
                            planName={planModel.name}
                            routingProjectId={routingProjectModel?.id}
                          />
                        ) : (
                          <PlanExportedStatus />
                        )}
                      </EmptyState.Container>
                    </Column>
                  </Row>
                )}
                {planLoading && (
                  <Row>
                    <Column>
                      <EmptyState.Container>
                        <Loader />
                      </EmptyState.Container>
                    </Column>
                  </Row>
                )}
                {datasetIsUploading && (
                  <Row>
                    <Column>
                      <EmptyState.Container>
                        <EmptyState.Description>
                          <FormattedMessage
                            id="routingProject.plan.dataset.empty.description"
                            defaultMessage="The locations are not uploaded yet."
                          />
                        </EmptyState.Description>
                        <EmptyState.Action>
                          <Button
                            type="button"
                            kind="primary"
                            small
                            onClick={(): void => {
                              refetchPlan()
                            }}
                          >
                            <FormattedMessage id="routingProject.plan.dataset.empty.action" defaultMessage="Retry" />
                          </Button>
                        </EmptyState.Action>
                      </EmptyState.Container>
                    </Column>
                  </Row>
                )}
                {datasetReady && (
                  <>
                    {canSelectDow && (
                      <Row>
                        <Column>
                          <SelectDow selectedDow={selectedDow} setSelectedDow={setSelectedDow} planModel={planModel} />
                        </Column>
                      </Row>
                    )}
                    {noRoutesCreated && (
                      <Row>
                        <Column>
                          <WarningNotification>
                            <FormattedMessage
                              id="routingProjects.plan.error.noRoutesCreated"
                              defaultMessage="No route could be generated. Please check your constraints."
                            />
                          </WarningNotification>
                        </Column>
                      </Row>
                    )}
                    {canSelectOrphans && (
                      <Row>
                        <Column>
                          <OrphansLocationsCard
                            skippedLocationsCount={currentOptimizationCopy?.skippedLocations.length || 0}
                            onToggleOrphansLocationsList={handleToggleOrphansLocationsList}
                          />
                        </Column>
                      </Row>
                    )}
                    <Row>
                      <Column>
                        {!isEmpty(currentOptimizationCopy?.routes) ? (
                          <OptimizedRoutesList
                            routingProjectId={routingProjectModel.id}
                            currentOptimization={currentOptimizationCopy as IRoutingProjectPlanOptimization}
                            draggedLocation={draggedLocation}
                            onLocationRemove={handleLocationRemove}
                            planModel={planModelCopy}
                            setPlanModel={setPlanModel}
                          />
                        ) : (
                          <DatasetLocationsList
                            dataset={planModel.dataset as IRoutingProjectDataset}
                            selectedDow={selectedDow}
                          />
                        )}
                      </Column>
                    </Row>
                  </>
                )}
              </SidebarContent>
            )}
          </SidebarContainer>
          {isShowingOrphansLocationsList && canSelectOrphans && (
            <SidebarOverlay onClose={handleCloseOverlay}>
              <OrphansLocationsList
                skippedLocations={currentOptimizationCopy?.skippedLocations || []}
                draggedLocation={draggedLocation}
              />
            </SidebarOverlay>
          )}
          {overlayDisplayedRouteId && !isEmpty(overlayDisplayedRoute) && (
            <SidebarOverlay onClose={handleCloseOverlay}>
              <RouteItem
                routingProjectId={routingProjectModel.id}
                route={overlayDisplayedRoute}
                routeIndex={
                  currentOptimizationCopy?.routes.findIndex((route) => route.id === overlayDisplayedRouteId) as number
                }
                draggedLocation={draggedLocation}
                planModel={planModelCopy}
                setPlanModel={setPlanModel}
                currentOptimization={currentOptimizationCopy as IRoutingProjectPlanOptimization}
                onLocationRemove={handleLocationRemove}
              />
            </SidebarOverlay>
          )}
        </SidebarWrapper>
        {createPortal(
          <DragOverlay>
            {!isEmpty(draggedLocation) ? (
              <LocationItem
                location={draggedLocation.location}
                locationIndex={draggedLocation.locationIndex}
                routeIndex={draggedLocation.routeIndex}
                draggable
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
        {isOpenDragEndModal && (
          <LocationDragEndModal
            availableDows={availableDowsToApplyChange}
            onSubmit={handleApplyChangeOnDows}
            onClose={handleDragEndModalClose}
            onSuccess={handleDragEndModalSuccess}
          />
        )}
        {!isEmpty(locationIdToRemove) && (
          <LocationRemoveModal
            locationId={locationIdToRemove as string}
            onClose={(): void => {
              setLocationIdToRemove(null)
            }}
          />
        )}
      </DndContext>
    </SidebarContext.Provider>
  )
}
